import { takeUntil } from 'rxjs/operators';
import { Component, EventEmitter, HostBinding, Input, OnDestroy, Output, ViewChild, OnInit } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { AnalyticsService, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import {
  DeclineOffers,
  SellerOfferDto,
  InventoryItem,
  LaunchDarklyService,
  PurchaseActionService,
  SellErrors,
  DateUtilsService,
  UpdateOpenOfferCount,
  SetAuctionItemSold,
  AuctionItemState,
  DecrementOpenOffers, ConfigurationState

} from 'remarketing-angular-library';
import { HistoryComponent, OfferHistory } from '../history/history.component';
import { Select, Store } from '@ngxs/store';
import {
  BannerComponent,
  BannerConfig
} from '../../../sell-shared/components/banner/banner.component';
import { OfferActionInterface } from '../../interfaces/offer-action.interface';
import { get } from 'lodash';
import { HttpHeaders } from '@angular/common/http';
import { InventoryItemDataService } from './../../../sell-shared/services/inventory-item-data.service';
import { AcceptComponent } from '../accept/accept.component';
import { SellerOfferStatus } from 'remarketing-angular-library/enums/enums';
import { SharedlistsService } from '../../../../../shared/services/sharedlists/sharedlists.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  StateIndicatorComponent
} from '../../../../../shared/components/state-indicator/state-indicator.component';
import { DatalistItemComponent, TooltipComponent } from 'remarketing-ui-components-lib';
import { CounterComponent } from '../counter/counter.component';
import { DeclineComponent } from '../decline/decline.component';
import { ReviveComponent } from '../revive/revive.component';
import { WithdrawComponent } from '../withdraw/withdraw.component';
import { CommonModule } from '@angular/common';
import { DetailsQueries } from '../../../details/queries/details.queries';
import { VehicleDetailsQuery } from '../../../details/interfaces/vehicle-details.query';

@Component({
    selector: 'lib-offer',
    templateUrl: './offer.component.html',
    styleUrls: ['./offer.component.scss'],
    imports: [
        TranslocoDirective,
        BannerComponent,
        StateIndicatorComponent,
        DatalistItemComponent,
        TooltipComponent,
        AcceptComponent,
        HistoryComponent,
        CounterComponent,
        DeclineComponent,
        ReviveComponent,
        WithdrawComponent,
        CommonModule
    ]
})
export class OfferComponent implements OnInit, OnDestroy {
  @HostBinding('class.sell-offer--buyer-declined') get declined() {
    return (
      this.offer &&
      (this.offer.status === SellerOfferStatus.BUYER_DECLINED || this.offer.status === SellerOfferStatus.OFFER_DECLINED)
    );
  }
  private destroyed = new Subject();

  @Input() offer: Partial<SellerOfferDto & { history: OfferHistory }>;
  @Input() inventoryItem: InventoryItem;
  @Input() tenantName: string;
  @Input() index?: number;
  @Input() showBuyerUserNameToSeller: boolean;
  @Output() getHistory = new EventEmitter<any>();
  @ViewChild(AcceptComponent) acceptComponent: AcceptComponent;
  @Select(DetailsQueries.vehicleDetails) details$: Observable<VehicleDetailsQuery>;

  globalError$: Observable<boolean> = this.inventoryItemDataService.globalOfferError;
  globalError: boolean;
  translationPath: string;
  showBanner = false;
  auctionItemStatus: string = null;
  showDealershipName: boolean;
  isAdmin = false;

  banner: BannerConfig = {
    text: '',
    mode: 'none',
    showIcon: false,
    showUndo: false,
    showErrorIcon: false,
    showSuccessIcon: false
  };

  withdrewOffer = false;
  declinedOffer = false;
  acceptOffer = false;
  reviveOffer = false;
  counterOffer = false;

  private destroyed$ = new Subject();

  currentListType = '';
  offerListTooltipTitle = '';
  offerListTooltipBody = '';

  offerTranslations = {
    partOf: '',
    sharedList: '',
    customOffer: '',
    sharedListTooltipTitle: '',
    customOfferTooltipTitle: ''
  };

  constructor(
    private purchaseActionService: PurchaseActionService,
    private store: Store,
    private analyticsService: AnalyticsService,
    private readonly inventoryItemDataService: InventoryItemDataService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly _sharedListsService: SharedlistsService,
    private readonly dateUtilService: DateUtilsService,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly translocoService: TranslocoService
  ) {}

  readOnlyPrompt = 'Contact the Sales Manager to proceed on this offer.';

  /*
  Mapping coming in Sprint 20(?).
   */
  private statusMap = {
    [SellerOfferStatus.ACCEPTED]: { text: 'Accepted', variant: 'success' }, // green
    [SellerOfferStatus.BUYER_DECLINED]: { text: 'Buyer Declined', variant: 'seller-declined' },
    [SellerOfferStatus.BUYER_WITHDRAWN]: { text: 'Buyer Withdrawn', variant: 'seller-declined' },
    [SellerOfferStatus.CANCELLED]: { text: 'Cancelled', variant: 'seller-declined' },
    [SellerOfferStatus.BUYER_COUNTERED]: { text: 'Buyer Countered', variant: 'danger' }, // red
    [SellerOfferStatus.SELLER_DECLINED]: { text: 'Offer Declined', variant: 'seller-declined' },
    [SellerOfferStatus.OFFER_DECLINED]: { text: 'Offer Declined', variant: 'buyer-declined' },
    [SellerOfferStatus.DECLINED_WITH_LP]: { text: 'Offer Declined', variant: 'seller-declined' },
    [SellerOfferStatus.OFFER_EXPIRED]: { text: 'Offer Expired', variant: 'seller-declined' },
    [SellerOfferStatus.OFFER_FAILED]: { text: 'Offer Failed', variant: 'seller-declined' },
    [SellerOfferStatus.BUYER_OFFERED]: { text: 'Buyer Offered', variant: 'danger' }, // red
    [SellerOfferStatus.SELLER_COUNTERED]: { text: 'Seller Countered', variant: 'dark' }, // black
    [SellerOfferStatus.VEHICLE_REMOVED]: { text: 'Offer Cancelled', variant: 'seller-declined' },
    [SellerOfferStatus.SELLER_REVIVED]: { text: 'Seller Revived', variant: 'dark' }, // black
    [SellerOfferStatus.SELLER_WITHDRAWN]: { text: 'Seller Withdrawn', variant: 'seller-declined' },
    [SellerOfferStatus.NO_OFFER]: { text: 'No Offer', variant: 'seller-declined' },
    [SellerOfferStatus.SELLER_OFFERED]: { text: 'Seller Offered', variant: 'dark' }
  };

  private responseStatusMap = {
    ACCEPTED: SellerOfferStatus.ACCEPTED,
    BUYER_DECLINED: SellerOfferStatus.BUYER_DECLINED,
    BUYER_WITHDRAWN: SellerOfferStatus.BUYER_WITHDRAWN,
    CANCELLED: SellerOfferStatus.CANCELLED,
    BUYER_COUNTERED: SellerOfferStatus.BUYER_COUNTERED,
    SELLER_DECLINED: SellerOfferStatus.SELLER_DECLINED,
    DECLINED: SellerOfferStatus.SELLER_DECLINED,
    DECLINED_WITH_LP: SellerOfferStatus.DECLINED_WITH_LP,
    OFFER_EXPIRED: SellerOfferStatus.OFFER_EXPIRED,
    OFFER_FAILED: SellerOfferStatus.OFFER_FAILED,
    BUYER_OFFERED: SellerOfferStatus.BUYER_OFFERED,
    SELLER_COUNTERED: SellerOfferStatus.SELLER_COUNTERED,
    COUNTERED: SellerOfferStatus.SELLER_COUNTERED,
    VEHICLE_REMOVED: SellerOfferStatus.VEHICLE_REMOVED,
    SELLER_REVIVED: SellerOfferStatus.SELLER_REVIVED,
    REVIVED: SellerOfferStatus.SELLER_REVIVED,
    SELLER_WITHDRAWN: SellerOfferStatus.SELLER_WITHDRAWN,
    WITHDRAWN: SellerOfferStatus.SELLER_WITHDRAWN,
    NO_OFFER: SellerOfferStatus.NO_OFFER
  };

  ngOnInit() {
    this.globalError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(globalOfferError => (this.globalError = globalOfferError));

    this.store.select(AuctionItemState.auctionItem).subscribe(item => {
      if (item) {
        this.auctionItemStatus = item.auctionItemStatus;
      }
    });
    this.showDealershipName = this._launchDarklyService.getFeatureFlag('showBuyingDealershipName', false);
    this.offerListTooltipBody = this.getShareListVehicles();
    this.isAdmin = this.store.selectSnapshot(ConfigurationState.isAdmin());
    this.getTranslations();
    this.getOfferListTooltipTranslations();
  }

  get isInServiceVehiclesEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  getShareListVehicles() {
    return this._sharedListsService.getShareListVehicles();
  }

  get offerListsTooltip() {
    return this._sharedListsService.offerListsTooltip(this.offerListTooltipTitle, this.offerListTooltipBody);
  }

  get isOfferListsTooltipBodyEmpty() {
    return this._sharedListsService.isOfferListsTooltipBodyEmpty(this.offerListTooltipBody);
  }

  get status() {
    if (this.auctionItemStatus !== 'ACTIVE') {
      return { text: this.statusMap[this.offer.status].text, variant: 'grey' };
    }
    return this.statusMap[this.offer.status] || { text: '', variant: '' };
  }

  private _activeAction: string;
  set activeAction(action: string) {
    if (typeof action === 'undefined' && typeof this.focusTargetOnClose !== 'undefined') {
      // We're closing the modal - focus on the current target
      this.focusTargetOnClose.focus();
      this.focusTargetOnClose = undefined;
    }
    this._activeAction = action;
  }

  get activeAction() {
    return this._activeAction;
  }
  focusTargetOnClose: HTMLButtonElement;

  get hasActions() {
    return this.offer.offerActions.length > 0;
  }

  get canRespondToOffers() {
    return !this.offer.readOnly;
  }

  get offerDate() {
    return this.dateUtilService.localizeDateAndTime(this.offer.offerDate, true);
  }

  selectActionTab(event: MouseEvent, action: string) {
    this.inventoryItemDataService._globalErrorState.next({ message: null, showErrorBanner: false });
    this.inventoryItemDataService._localErrorState.next({ message: null, offerUuid: null });
    this.inventoryItemDataService._globalOfferError.next(false);

    if (event.target) {
      setTimeout(() => {
        (event.target as HTMLElement).parentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
    // Close by clicking the same button
    if (this.activeAction === action) {
      this.activeAction = undefined;
    } else {
      // Close current tab and open a new one
      this.focusTargetOnClose = event.currentTarget as HTMLButtonElement;
      this.activeAction = action;
      if (action === 'VIEW_HISTORY') {
        this.getHistory.emit(this.offer);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  formatCurrency(currency) {
    return this.currencyFormatService.formatCurrency(currency, false).replaceAll('-', '');
  }

  handleOfferAction(data: OfferActionInterface) {
    let headers = new HttpHeaders();
    if (data.acceptLowerOffer) {
      headers = headers.append('X-IGNORE-WARNING', 'ACCEPT_LOWER_OFFER');
    }
    const { comment: existingComment } = data.payload;
    // Ensure that a blank string isn't sent as the comment
    const comment = existingComment ? existingComment : null;
    this.purchaseActionService
      .sellerOfferAction(data.payload.offerUuid, { ...data.payload, comment }, headers)
      .subscribe(
        response => {
          data.analyticsPayload.formValues.status = 'success';

          this.handleOfferAnalytics(data.analyticsPayload);

          this.offer.status = this.responseStatusMap[response.status];
          this.offer.offerActions = [];
          this.activeAction = null;
          // update the offer count on manage inventory page
          this.store.dispatch(new UpdateOpenOfferCount(this.inventoryItem.inventoryItemUuid));

          if (data.payload.requestedAction === 'ACCEPT') {
            // make other offers declined
            this.store.dispatch(new DeclineOffers(this.offer.offerUuid));
            // set inventory item as sold, set open uestions and offers to 0
            this.store.dispatch(new SetAuctionItemSold());
            // success message
            this.showBanner = true;
            this.banner.showIcon = true;
            this.banner.showSuccessIcon = true;
            this.banner.mode = 'confirmation';
            this.translationPath = 'sellModule.offers.accept.offerAcceptedSuccessMessage';
            this.acceptOffer = true;
            this.analyticsService.fireBuyActionEvent('acceptBuyAction', 1, [this.inventoryItem.auctionItemUuid]);
          } else if (data.payload.requestedAction === 'COUNTER') {
            // update the offer count on inventory card
            this.store.dispatch(new DecrementOpenOffers());
            // update the offer amount and title on offer card
            this.offer.amount = data.payload.amount;
            this.counterOffer = true;
            this.analyticsService.fireBuyActionEvent('counterBuyAction', 1, [this.inventoryItem.auctionItemUuid]);
          } else if (data.payload.requestedAction === 'REVIVE') {
            // update the offer amount and title on offer card
            this.offer.amount = data.payload.amount;
            this.reviveOffer = true;
            this.analyticsService.fireBuyActionEvent('reviveBuyAction', 1, [this.inventoryItem.auctionItemUuid]);
          } else if (data.payload.requestedAction === 'DECLINE') {
            // update the offer count on inventory card
            this.store.dispatch(new DecrementOpenOffers());
            this.declinedOffer = true;
            this.analyticsService.fireBuyActionEvent('declineBuyAction', 1, [this.inventoryItem.auctionItemUuid]);
          } else if (data.payload.requestedAction === 'WITHDRAW') {
            this.withdrewOffer = true;
            this.analyticsService.fireBuyActionEvent('withdrawBuyAction', 1, [this.inventoryItem.auctionItemUuid]);
          }
        },
        error => {
          data.analyticsPayload.formValues.status = 'error';
          data.analyticsPayload.formValues.errors = [error.message];
          this.handleFormTracking(data.analyticsPayload);

          if (data.payload.requestedAction === 'ACCEPT' && get(error, 'error.errors[0]').code === 'F-REMPLT-AIS-ONH') {
            this.acceptComponent.offerResponseErrorCode(get(error, 'error.errors[0]').code);
          } else {
            const errorState = SellErrors.fromSellErrors(get(error, 'error.errors[0]').code);
            switch (errorState.type) {
              case 'Tray': {
                this.localErrorHandler(errorState.message);
                break;
              }
              case 'Refresh': {
                this.globalErrorHandler(errorState.message);
                break;
              }
            }
          }
        }
      );
  }

  handleOfferAnalytics(payload) {
    this.analyticsService.track('customEvent', {
      name: 'Offer Event',
      attr: payload
    });
  }

  handleFormTracking(payload) {
    this.analyticsService.track('customError', payload.formValues.errors, null, payload);
  }

  closeBanner() {
    this.showBanner = false;
  }

  globalErrorHandler(errorMessage: string) {
    this.activeAction = undefined;

    this.inventoryItemDataService._globalErrorState.next({ message: errorMessage, showErrorBanner: true });
    this.inventoryItemDataService._globalOfferError.next(true);

    this.inventoryItemDataService.fetchBasicDetails();
  }

  localErrorHandler(errorMessage: string) {
    const payload = { message: errorMessage, offerUuid: this.offer.offerUuid };
    this.inventoryItemDataService._localErrorState.next(payload);
  }

  getOfferListTooltipTranslations() {
    this.details$.subscribe(details => {
      if (details) {
        if (this.isInServiceVehiclesEnabled && details?.typeCode === 'ACP') {
          this.currentListType = this.offerTranslations.customOffer;
          this.offerListTooltipTitle = this.offerTranslations.customOfferTooltipTitle;
        } else {
          this.currentListType = this.offerTranslations.sharedList;
          this.offerListTooltipTitle = this.offerTranslations.sharedListTooltipTitle;
        }
      }
      this.offerListTooltipBody = this.getShareListVehicles();
    });
  }

  getTranslations() {
    const keyParams = {
      'offerList.general': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translation => {
        this.offerTranslations.partOf = translation[0].partOf;
        this.offerTranslations.sharedList = translation[0].sharedList;
        this.offerTranslations.customOffer = translation[0].customOffer;
        this.offerTranslations.sharedListTooltipTitle = translation[0].offerListToolTipTitle;
        this.offerTranslations.customOfferTooltipTitle = translation[0].customOfferToolTipTitle;
      });
  }
}
